import {
  OMNI_STRINGS,
  omniAnnounceAll,
  omniAnnounceFiltered,
  omniSelectionTooltipAll,
  resolveVisibleErgs,
  resolveVisibleSites,
} from '../omnidashboard.config';
import { ErgRow, SiteColumn } from '../omnidashboard.models';
import { OmniFilterState, ResolvedOmniFilters } from './omnidashboard-filters.model';

export function sanitizeFilterSelections(
  filters: OmniFilterState,
  siteColumns: SiteColumn[],
  ergRows: ErgRow[]
): OmniFilterState {
  const validSites = new Set(siteColumns.map((site) => site.siteName));
  const validErgs = new Set(ergRows.map((erg) => erg.bpgName));
  return {
    selectedSiteNames: filters.selectedSiteNames.filter((name) => validSites.has(name)),
    selectedErgNames: filters.selectedErgNames.filter((name) => validErgs.has(name)),
  };
}

function buildSelectionTooltip(
  selected: string[],
  totalCount: number,
  singular: string,
  plural: string
): string {
  if (!selected.length) {
    return '';
  }
  if (selected.length >= totalCount) {
    return totalCount === 1 ? `1 ${singular}` : omniSelectionTooltipAll(totalCount, plural);
  }
  return selected.join(', ');
}

function isFilterActive(
  selectedSiteNames: string[],
  selectedErgNames: string[],
  siteColumns: SiteColumn[],
  ergRows: ErgRow[]
): boolean {
  // No selection means "show all"; active only when a strict subset is chosen.
  const sitesFiltered =
    selectedSiteNames.length > 0 && selectedSiteNames.length < siteColumns.length;
  const ergsFiltered =
    selectedErgNames.length > 0 && selectedErgNames.length < ergRows.length;
  return sitesFiltered || ergsFiltered;
}

function hasNoVisibleMatrix(
  ergRows: ErgRow[],
  visibleSiteColumns: SiteColumn[],
  visibleErgRows: ErgRow[]
): boolean {
  return ergRows.length > 0 && (
    visibleSiteColumns.length === 0 || visibleErgRows.length === 0
  );
}

function buildFilterAnnounceMessage(
  hasNoMatch: boolean,
  isActive: boolean,
  visibleErgRows: ErgRow[],
  visibleSiteColumns: SiteColumn[],
  visibleChapterCount: number
): string {
  if (hasNoMatch) {
    return OMNI_STRINGS.announceNoMatch;
  }
  const ergCount = visibleErgRows.length;
  const siteCount = visibleSiteColumns.length;
  return isActive
    ? omniAnnounceFiltered(ergCount, siteCount, visibleChapterCount)
    : omniAnnounceAll(ergCount, siteCount, visibleChapterCount);
}

export function resolveOmniFilters(
  filters: OmniFilterState,
  siteColumns: SiteColumn[],
  ergRows: ErgRow[],
  visibleChapterCount: number
): ResolvedOmniFilters {
  const sanitized = sanitizeFilterSelections(filters, siteColumns, ergRows);
  const visibleSiteColumns = resolveVisibleSites(siteColumns, sanitized.selectedSiteNames);
  const visibleErgRows = resolveVisibleErgs(ergRows, sanitized.selectedErgNames);
  const active = isFilterActive(
    sanitized.selectedSiteNames,
    sanitized.selectedErgNames,
    siteColumns,
    ergRows
  );
  const noVisibleMatrix = hasNoVisibleMatrix(ergRows, visibleSiteColumns, visibleErgRows);

  return {
    selectedSiteNames: sanitized.selectedSiteNames,
    selectedErgNames: sanitized.selectedErgNames,
    visibleSiteColumns,
    visibleErgRows,
    siteFilterTooltip: buildSelectionTooltip(
      sanitized.selectedSiteNames,
      siteColumns.length,
      'site',
      'sites'
    ),
    ergFilterTooltip: buildSelectionTooltip(
      sanitized.selectedErgNames,
      ergRows.length,
      'ERG',
      'ERGs'
    ),
    isFilterActive: active,
    hasNoVisibleMatrix: noVisibleMatrix,
    filterAnnounceMessage: buildFilterAnnounceMessage(
      noVisibleMatrix,
      active,
      visibleErgRows,
      visibleSiteColumns,
      visibleChapterCount
    ),
  };
}
